/* LEGAL-NOTICE
   © Copyright 2018 - Quid Informatica S.p.A. - All Rights Reserved
   https://quidinfo.it/qinetic/licence.html - "Quid Consumer Lending Platform"
   Deposito SIAE n. 012702 del 26 Luglio 2018
LEGAL-NOTICE */
export class ExpressionEvaluator {
  /**
   * options
   */
  private options: Options;

  /**
   * valore dell'expression mantenuto immutato
   */
  private expression: string;

  /**
   * valore temporaneo dell'expression utilizzato per applicare la sostituzione dei placeholder;
   * viene rigenerato a partire dall'expression originale ogni volta che si fa un eval
   */
  private tempExpression: string;

  /**
   * model da cui vengono estratti i valori dei placeholder al momento dell'eval
   */
  private model: EvalModel;

  /**
   * chiave per indicare a quale valore del model l'espressione deve essere uguale
   */
  private equalToKey: string;

  constructor(expression: any, options?: Options) {
    this.setMatrixExpression(expression);
    this.setOptions(options);
  }

  /**
   * valuta l'expression in base al model passato in argomento
   */
  eval(model: EvalModel, equalToKey?: string): boolean {
    this.setEvalEnv(model, equalToKey);

    let valid = false;

    if (this.isNull()) {
      valid = true;
    } else if (this.isTrue()) {
      valid = true;
    } else if (this.isFalse()) {
      valid = false;
    } else if (this.isValid()) {
      valid = true;
    }

    this.cleanEvalEnv();

    return valid;
  }

  /**
   * controlla se l'expression contiene la key richiesta
   */
  includes(key: string): boolean {
    if (typeof this.expression !== 'string' || !this.expression || !key) {
      return false;
    }

    const placeholder = this.options.placeholderHook + key;

    return this.expression.indexOf(placeholder) > -1;
  }

  /**
   * stabilisce se l'espressione è ammissibile
   */
  isEligible(): boolean {
    return !!this.expression;
  }

  /**
   * imposta la stringa di expression
   */
  private setMatrixExpression(expression: any): void {
    if (
      typeof expression === 'string' ||
      typeof expression === 'number' ||
      typeof expression === 'boolean'
    ) {
      this.expression = '' + expression;
    } else {
      this.expression = null;
    }
  }

  /**
   * imposta le options
   */
  private setOptions(options: Options): void {
    this.options = {
      placeholderHook: (options && options.placeholderHook) || '$',
      trueAlias: (options && options.trueAlias) || 'true',
      falseAlias: (options && options.falseAlias) || 'false'
    };
  }

  /**
   * copia l'expression sulla proprietà tempExpression su cui verranno
   * sostituiti i placeholder con i valori presenti nel model ad ogni eval
   */
  private setEvalEnv(model: EvalModel, equalToKey?: string): void {
    this.tempExpression = this.expression;
    this.model = { ...model };
    this.equalToKey = equalToKey || null;
  }

  /**
   * pulisce le proprietà di appoggio utilizzate per l'eval
   * per liberare memoria
   */
  private cleanEvalEnv(): void {
    this.tempExpression = null;
    this.model = null;
    this.equalToKey = null;
  }

  /**
   * restituisce la temp expression applicando trim e lower case
   */
  private getFlatExpression(): string {
    if (typeof this.tempExpression === 'string') {
      return this.tempExpression.trim().toLowerCase();
    } else {
      return this.tempExpression;
    }
  }

  /**
   * controlla che l'expression sia null
   */
  private isNull(): boolean {
    return this.tempExpression === null || this.tempExpression === 'null';
  }

  /**
   * controlla che l'expression sia true
   */
  private isTrue(): boolean {
    const flatVal = this.getFlatExpression();

    return flatVal === this.options.trueAlias.toLowerCase();
  }

  /**
   * controlla che l'expression sia false
   */
  private isFalse(): boolean {
    const flatVal = this.getFlatExpression();

    return flatVal === this.options.falseAlias.toLowerCase();
  }

  /**
   * verifica la validità dell'expression sostituendo tutti i placeholder
   * con i relativi valore nel model ed eseguendo l'eval
   */
  private isValid(): boolean {
    if (!!this.equalToKey && !this.expressionHasPlaceholders()) {
      return this.isEqual();
    }

    // sostituisco ogni placeholder
    while (this.expressionHasPlaceholders()) {
      this.replaceNextPlaceholder();
    }

    let valid = false;

    try {
      // eslint-disable-next-line no-eval
      valid = eval(this.tempExpression); //NOSONAR
    } catch (_) {
      console.log(_);
    }

    return valid;
  }

  /**
   * controlla se il valore dell'espressione sia esattamente uguale al valore
   * corrispondente al model indicato tramite equalToKey
   */
  private isEqual(): boolean {
    return this.tempExpression === this.findKeyValue(this.equalToKey);
  }

  /**
   * true se l'expression contiene almeno un carattere che identifica l'inizio di un placeholder
   */
  private expressionHasPlaceholders(): boolean {
    return this.tempExpression.indexOf(this.options.placeholderHook) > -1;
  }

  /**
   * sostituisce il primo placeholder che trova nell'expression
   * con il relativo valore che trova nel model
   */
  private replaceNextPlaceholder(): void {
    // trovo il primo placeholder presente nell'expression (es: "$NOME_CAMPO")
    const placeholder = findFirstPlaceholder(
      this.tempExpression,
      this.options.placeholderHook
    );

    if (!placeholder) {
      return;
    }

    this.replacePlaceholder(placeholder);
  }

  /**
   * sostituisce il placeholder che trova nell'expression
   * con il relativo valore nel model
   */
  private replacePlaceholder(placeholder: string): void {
    // recupero il valore associato al campo
    const replaceValue = this.getPlaceholderReplaceValue(placeholder);

    // sostituisco nell'expression il placeholder con il suo valore
    this.tempExpression = this.tempExpression.replace(
      placeholder,
      replaceValue
    );
  }

  /**
   * recupera il valore del model corrispondente alla key e lo prepara
   * per essere sostituito al placeholder
   */
  private getPlaceholderReplaceValue(placeholder: string): string {
    // definisco la key da ricercare nel model rimuovendo il carattere di hook (es: "NOME_CAMPO")
    const key = placeholder.substring(this.options.placeholderHook.length);
    const value = this.findKeyValue(key);

    return wrapReplaceValue(value);
  }

  /**
   * trova nel model il valore  corrispondente alla key
   */
  private findKeyValue(key: string): string {
    if (
      !!this.model &&
      (typeof this.model[key] === 'string' ||
        typeof this.model[key] === 'boolean' ||
        typeof this.model[key] === 'number')
    ) {
      return this.model[key];
    } else {
      return null;
    }
  }
}

/**
 * trova il primo placeholder contenuto nelle stringa
 */
export const findFirstPlaceholder = (str: string, hook: string): string => {
  const hookIndex = str.indexOf(hook);

  if (hookIndex < 0) {
    return null;
  }

  const spaceIndex = str.substring(hookIndex).indexOf(' ');

  const start = hookIndex;
  const end = spaceIndex < 0 ? str.length : spaceIndex + hookIndex;

  return str.substring(start, end);
};

/**
 * wrappa la stringa aggiungendo gli apici
 */
export const wrapReplaceValue = (value: string): string => {
  if (typeof value === 'string') {
    // eslint-disable-next-line @typescript-eslint/quotes
    return `'${value.replace(/'/g, "\\'")}'`;
  } else if (typeof value === 'number' || typeof value === 'boolean') {
    return value;
  } else {
    return null;
  }
};

export interface EvalModel {
  [key: string]: string;
}

export interface Options {
  /**
   * identifica il carattere utilizzato per rilevare il placeholder all'interno dell'expression
   */
  placeholderHook?: string;

  /**
   * indentifica il value che deve assumere l'expression per essere considerata true
   */
  trueAlias?: string;

  /**
   * indentifica il value che deve assumere l'expression per essere considerata false
   */
  falseAlias?: string;
}
